import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useQueryClient, type QueryKey } from '@tanstack/react-query';

export const COALESCE_WINDOW_MS = 2000;

export interface CoalescedInvalidation {
  invalidate: (extraKeys?: readonly QueryKey[]) => void;
  flush: () => Promise<void>;
}

export function useCoalescedInvalidation(
  queryKeys: readonly QueryKey[],
  options?: { windowMs?: number }
): CoalescedInvalidation {
  const queryClient = useQueryClient();
  const windowMs = options?.windowMs ?? COALESCE_WINDOW_MS;

  const baseKeysSignature = JSON.stringify(queryKeys);
  const baseKeys = useMemo(() => queryKeys, [baseKeysSignature]); // eslint-disable-line react-hooks/exhaustive-deps

  const pendingRef = useRef<Map<string, QueryKey>>(new Map());
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const drain = useCallback((): Promise<void> => {
    if (timerRef.current !== null) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    const keys = [...pendingRef.current.values()];
    pendingRef.current.clear();
    if (keys.length === 0) return Promise.resolve();
    return Promise.all(keys.map((queryKey) => queryClient.invalidateQueries({ queryKey }))).then(
      () => undefined
    );
  }, [queryClient]);

  const invalidate = useCallback(
    (extraKeys?: readonly QueryKey[]) => {
      for (const key of [...baseKeys, ...(extraKeys ?? [])]) {
        pendingRef.current.set(JSON.stringify(key), key);
      }
      // Windowed throttle, not a debounce: the timer is armed by the first pending key of a
      // window and is deliberately NOT reset by later calls. Resetting it would starve the
      // flush for the whole of a run that emits faster than windowMs, freezing the page until
      // the run ended.
      if (timerRef.current !== null) return;
      timerRef.current = setTimeout(() => {
        timerRef.current = null;
        void drain();
      }, windowMs);
    },
    [baseKeys, drain, windowMs]
  );

  const flush = useCallback(() => drain(), [drain]);

  useEffect(() => {
    return () => {
      void drain();
    };
  }, [drain]);

  return useMemo(() => ({ invalidate, flush }), [invalidate, flush]);
}
